import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';

export type PaymentLineItem = {
  id: string;
  item_type: 'service' | 'supplement' | 'other';
  service_id?: string | null;
  product_id?: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  gross_amount: number;
  discount_type?: string | null;
  discount_value?: number;
  discount_amount?: number;
  discount_reason?: string | null;
  net_amount?: number;
  product?: {
    name: string;
    brand?: string;
    form?: string;
    dosage?: string;
  } | null;
};

export type PaymentRecord = {
  id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'paid' | 'expired';
  payment_method: string | null;
  transaction_id: string | null;
  notes: string | null;
  due_at: string;
  paid_at: string | null;
  appointment_id: string | null;
  input_mode?: 'quick' | 'detailed';
  subtotal?: number | null;
  overall_discount_type?: string | null;
  overall_discount_value?: number | null;
  overall_discount_amount?: number | null;
  overall_discount_reason?: string | null;
  tax_enabled?: boolean;
  tax_rate?: number;
  tax_amount?: number;
  created_at: string | null;
  updated_at: string | null;
  line_items?: PaymentLineItem[];
};

async function fetchPayments(
  clientId: string,
  accessToken: string | null | undefined,
) {
  return apiRequest<PaymentRecord[]>(
    `/api/clients/${clientId}/payments`,
    { method: 'GET' },
    accessToken,
  );
}

export function useClientPayments(clientId: string) {
  const { session } = useAuth();

  return useQuery<PaymentRecord[]>({
    queryKey: ['payments', clientId],
    enabled: Boolean(clientId && session),
    queryFn: () => fetchPayments(clientId, session?.access_token),
  });
}

export function useInvalidatePayments(clientId: string) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ['payments', clientId],
    });
}
